import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Drawer from "@/components/ui/drawer";
import Chip from "@/components/ui/chip";
import {
  Eye as EyeIcon,
  Download as DownloadIcon,
  User as UserIcon,
  Category as CategoryIcon,
  Document as DocumentIcon,
  Asset as AssetIcon,
} from "@/components/icons";
import type { AuditLog } from "../types/logs.types";
import { logsApi, toAuditLogLabel } from "../services/logs.api";
import { humanizeBackendError } from "../utils/errorMessageMap";
import styles from "./LogDetailDrawer.module.scss";

function getActionLabel(action: string, t: (k: string) => string): string {
  const key = String(action).toUpperCase();
  const i18nKey = `logs.actionLabels.${key}`;
  const label = t(i18nKey);
  if (label !== i18nKey) return label;
  return action || "—";
}

function formatLogDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

function formatLogTime(iso: string): string {
  const d = new Date(iso);
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

function isFailedStatus(log: AuditLog): boolean {
  const raw = String(log.status ?? "").toUpperCase();
  if (raw === "FAILED" || raw === "ERROR") return true;
  const code = log.statusCode ?? 0;
  return code >= 400;
}

function getAttemptedUsername(log: AuditLog): string {
  const meta = log.metadata as Record<string, unknown> | null | undefined;
  const u = meta && typeof meta === "object" ? meta["attemptedUsername"] : null;
  return typeof u === "string" && u.trim() ? u.trim() : "";
}

/**
 * Drawer için kullanıcı dostu özet cümle. LogsPage `buildLogDescription` ile
 * benzer mantık ama drawer bağımsız çalışsın diye ayrı tutuluyor (i18n key
 * setleri aynı; ham mesaj asla gösterilmez).
 */
function buildSummary(
  log: AuditLog,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  const action = String(log.action ?? "").toUpperCase();
  if (action === "LOGIN") {
    const username = getAttemptedUsername(log) || log.userDisplayName || "—";
    if (isFailedStatus(log)) {
      return t("logs.descriptions.loginFailed", { username });
    }
    return username !== "—"
      ? t("logs.descriptions.loginSuccessNamed", { username })
      : t("logs.descriptions.loginSuccess");
  }
  if (action === "LOGOUT") return t("logs.descriptions.logoutSuccess");
  if (isFailedStatus(log)) {
    return humanizeBackendError(log.errorMessage, log.statusCode, t);
  }
  return log.endpointName || t("logs.detail.summaryEmpty");
}

export interface LogDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  logId: string | null;
}

const LogDetailDrawer: React.FC<LogDetailDrawerProps> = ({
  isOpen,
  onClose,
  logId,
}) => {
  const { t } = useTranslation();
  const [log, setLog] = useState<AuditLog | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTechnical, setShowTechnical] = useState(false);
  const [showRawError, setShowRawError] = useState(false);

  useEffect(() => {
    if (!isOpen || !logId) {
      setLog(null);
      setError(null);
      setShowTechnical(false);
      setShowRawError(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    logsApi
      .getById(logId)
      .then((data) => {
        if (!cancelled) setLog(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message ?? t("logs.loadError"));
          setLog(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, logId, t]);

  const summaryText = useMemo(() => (log ? buildSummary(log, t) : ""), [log, t]);

  if (!logId) return null;

  const logIdBadge = `#LOG-${logId.slice(0, 8).toUpperCase()}`;
  const headerTitle = (
    <span className={styles.headerTitle}>
      <EyeIcon width={24} height={24} color="#14181F" />
      {t("logs.detail.title")}
    </span>
  );

  const handleDownload = () => {
    if (!log) return;
    const blob = new Blob([JSON.stringify(log, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `log-${logId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const headerActions = (
    <div className={styles.headerActions}>
      <button
        type="button"
        className={styles.headerActionButton}
        onClick={handleDownload}
        disabled={!log || isLoading}
        aria-label={t("buttons.download")}
      >
        <DownloadIcon width={20} height={20} color="#34314A" />
      </button>
    </div>
  );

  const renderSummaryBlock = (l: AuditLog) => {
    const attemptedUsername = getAttemptedUsername(l);
    const items: Array<{ label: string; value: string; icon: React.ReactNode }> = [
      {
        icon: <UserIcon width={18} height={18} />,
        label: t("logs.detail.user"),
        value: l.userDisplayName || "—",
      },
      {
        icon: <CategoryIcon width={18} height={18} />,
        label: t("logs.detail.module"),
        value: toAuditLogLabel(l.module),
      },
    ];
    if (attemptedUsername) {
      items.push({
        icon: <UserIcon width={18} height={18} />,
        label: t("logs.detail.attemptedUsername"),
        value: attemptedUsername,
      });
    }
    return (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <DocumentIcon width={18} height={18} color="#34314A" />
          <span>{t("logs.detail.summary")}</span>
        </div>
        <div className={styles.sectionBody}>
          <p className={styles.summaryText}>{summaryText}</p>
          <div className={styles.detailList}>
            {items.map((item, index) => (
              <div key={index} className={styles.detailItem}>
                <div className={styles.detailLabel}>
                  <span className={styles.detailIcon}>{item.icon}</span>
                  <span>{item.label}</span>
                </div>
                <div className={styles.detailValue}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  const renderAffectedRecord = (l: AuditLog) => {
    const hasEntity = !!(l.entityType || l.entityID || l.relatedRecordUrl);
    if (!hasEntity) return null;
    return (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <AssetIcon width={18} height={18} color="#34314A" />
          <span>{t("logs.detail.affectedRecord")}</span>
        </div>
        <div className={styles.sectionBody}>
          <div className={styles.detailList}>
            {l.entityType && (
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>
                  <span>{t("logs.detail.entityType")}</span>
                </div>
                <div className={styles.detailValue}>
                  {toAuditLogLabel(l.entityType)}
                </div>
              </div>
            )}
            {l.entityID && (
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>
                  <span>{t("logs.detail.entityID")}</span>
                </div>
                <div className={styles.detailValue}>{l.entityID}</div>
              </div>
            )}
            {l.relatedRecordUrl && (
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>
                  <span>{t("logs.detail.relatedRecordUrl")}</span>
                </div>
                <div className={styles.detailValue}>
                  <a
                    href={l.relatedRecordUrl}
                    className={styles.recordLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("logs.detail.affectedRecordView")}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  const renderTechnicalDetail = (l: AuditLog) => {
    const rows: Array<{ label: string; value: string }> = [
      {
        label: t("logs.detail.requestMethod"),
        value: l.requestMethod || "—",
      },
      {
        label: t("logs.detail.requestPath"),
        value: l.requestPath || "—",
      },
      {
        label: t("logs.detail.statusCode"),
        value: l.statusCode != null ? String(l.statusCode) : "—",
      },
      {
        label: t("logs.detail.duration"),
        value: l.duration != null ? String(l.duration) : "—",
      },
      {
        label: t("logs.detail.ipAddress"),
        value: l.ipAddress || "—",
      },
      {
        label: t("logs.detail.requestId"),
        value: l.requestId || "—",
      },
      {
        label: t("logs.detail.device"),
        value: l.userAgent || "—",
      },
    ];
    if (l.endpointName) {
      rows.unshift({
        label: t("logs.detail.endpointName"),
        value: l.endpointName,
      });
    }
    return (
      <section className={styles.section}>
        <button
          type="button"
          className={styles.collapsibleHeader}
          onClick={() => setShowTechnical((v) => !v)}
          aria-expanded={showTechnical}
        >
          <span className={styles.sectionHeaderInner}>
            <DocumentIcon width={18} height={18} color="#34314A" />
            <span>{t("logs.detail.technicalDetail")}</span>
          </span>
          <span className={styles.collapsibleArrow} data-open={showTechnical}>
            ▾
          </span>
        </button>
        {showTechnical && (
          <div className={styles.sectionBody}>
            <p className={styles.collapsibleHint}>
              {t("logs.detail.technicalDetailHint")}
            </p>
            <div className={styles.detailList}>
              {rows.map((row, index) => (
                <div key={index} className={styles.detailItem}>
                  <div className={styles.detailLabel}>
                    <span>{row.label}</span>
                  </div>
                  <div className={styles.detailValue}>{row.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    );
  };

  const renderErrorDetail = (l: AuditLog) => {
    if (!isFailedStatus(l)) return null;
    const friendly = humanizeBackendError(l.errorMessage, l.statusCode, t);
    const hasRaw = !!l.errorMessage;
    const hasMeta = !!(
      l.metadata &&
      typeof l.metadata === "object" &&
      Object.keys(l.metadata).length > 0
    );

    return (
      <section className={`${styles.section} ${styles.errorSection}`}>
        <div className={styles.sectionHeader}>
          <DocumentIcon width={18} height={18} color="#c62828" />
          <span>{t("logs.detail.errorDetail")}</span>
        </div>
        <div className={styles.sectionBody}>
          <p className={styles.errorOneLiner}>{friendly}</p>
          {(hasRaw || hasMeta) && (
            <button
              type="button"
              className={styles.developerToggle}
              onClick={() => setShowRawError((v) => !v)}
              aria-expanded={showRawError}
            >
              {showRawError
                ? t("logs.detail.hideDeveloperDetails")
                : t("logs.detail.showDeveloperDetails")}
            </button>
          )}
          {showRawError && hasRaw && (
            <div className={styles.developerBlock}>
              <span className={styles.developerLabel}>
                {t("logs.detail.rawError")}
              </span>
              <pre className={styles.developerBody}>{l.errorMessage}</pre>
            </div>
          )}
          {showRawError && hasMeta && (
            <div className={styles.developerBlock}>
              <span className={styles.developerLabel}>
                {t("logs.detail.additionalInfo")}
              </span>
              <pre className={styles.developerBody}>
                {JSON.stringify(l.metadata, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </section>
    );
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={headerTitle}
      titleBadge={logIdBadge}
      headerActions={headerActions}
      width="568px"
      position="right"
    >
      <div className={styles.drawerContent}>
        {isLoading && (
          <div className={styles.loadingState}>
            {t("common.loading")}
          </div>
        )}
        {error && <div className={styles.errorState}>{error}</div>}
        {!isLoading && !error && log && (
          <>
            <div className={styles.topRow}>
              <div className={styles.topItem}>
                <span className={styles.topLabel}>
                  {t("logs.detail.date")}
                </span>
                <span className={styles.topValue}>
                  {formatLogDate(log.createdAt)}
                </span>
              </div>
              <div className={styles.topItem}>
                <span className={styles.topLabel}>
                  {t("logs.detail.time")}
                </span>
                <span className={styles.topValue}>
                  {formatLogTime(log.createdAt)}
                </span>
              </div>
              <div className={styles.topItem}>
                <span className={styles.topLabel}>
                  {t("logs.detail.operationType")}
                </span>
                <span className={styles.operationChip}>
                  <Chip type="primary" variant="soft" size="small">
                    {getActionLabel(log.action, t)}
                  </Chip>
                </span>
              </div>
            </div>

            {renderSummaryBlock(log)}
            {renderErrorDetail(log)}
            {renderAffectedRecord(log)}
            {renderTechnicalDetail(log)}
          </>
        )}
      </div>
    </Drawer>
  );
};

export default LogDetailDrawer;
